import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * ReferralPage - Page qui permet à un destinataire de rediriger l'analyse vers un collègue plus approprié
 */
const ReferralPage: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [referralEmail, setReferralEmail] = useState<string>('');
  const [prospectInfo, setProspectInfo] = useState<{
    id: string;
    name: string;
    company: string | null;
  } | null>(null);

  // Charger les informations du prospect à partir du token
  useEffect(() => {
    const fetchProspect = async () => {
      try {
        if (!token) {
          setError('Token invalide ou manquant');
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('prospects')
          .select('id, prenom, nom, email, metadata')
          .eq('token', token)
          .single();

        if (error || !data) {
          setError('Ce lien a expiré ou est invalide.');
          setLoading(false);
          return;
        }

        setProspectInfo({
          id: data.id,
          name: `${data.prenom} ${data.nom}`,
          company: data.metadata?.company || null,
        });
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        setError('Une erreur est survenue lors du chargement de la page.');
        setLoading(false);
      }
    };

    fetchProspect();
  }, [token]);

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (!referralEmail || !prospectInfo?.id) {
        setError('Email invalide ou informations manquantes.');
        setSubmitting(false);
        return;
      }

      // Récupérer d'abord les métadonnées existantes du prospect
      const { data: prospectData } = await supabase
        .from('prospects')
        .select('metadata, email')
        .eq('id', prospectInfo.id)
        .single();

      // Mise à jour des métadonnées avec l'email référé
      const updatedMetadata = {
        ...((prospectData?.metadata as object) || {}),
        referred_email: referralEmail,
        referred_at: new Date().toISOString()
      };

      // Mise à jour du prospect dans la base de données
      const { error } = await supabase
        .from('prospects')
        .update({
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', prospectInfo.id);

      if (error) {
        throw error;
      }

      // Enregistrer cette référence dans une nouvelle table spécifique
      await supabase.from('referrals').insert({
        prospect_id: prospectInfo.id,
        original_email: prospectData?.email || null,
        referred_email: referralEmail,
        status: 'pending',
        created_at: new Date().toISOString(),
      });

      setSuccess(true);
    } catch (err) {
      console.error('Erreur lors de la soumission:', err);
      setError('Une erreur est survenue lors de l\'enregistrement. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center p-8">
          <Loader className="h-10 w-10 text-amber-500 animate-spin mb-4" />
          <p className="text-gray-600">Chargement en cours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-center text-gray-800 mb-4">Oups !</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-amber-500 text-white py-2 px-4 rounded-md hover:bg-amber-600 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-center text-gray-800 mb-4">
            Merci pour votre aide !
          </h2>
          <p className="text-center text-gray-600 mb-6">
            Nous allons contacter votre collègue pour lui proposer une analyse personnalisée.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-amber-500 text-white py-2 px-4 rounded-md hover:bg-amber-600 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          Rediriger l'analyse
        </h2>
        
        {prospectInfo?.company && (
          <p className="text-gray-600 mb-4">
            Notre analyse pour <span className="font-semibold">{prospectInfo.company}</span> n'est pas pertinente pour vous ?
          </p>
        )}
        
        <p className="text-gray-600 mb-6">
          Merci de nous aider à contacter la personne en charge du marketing/communication dans votre organisation.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="referralEmail" className="block text-sm font-medium text-gray-700 mb-1">
              Email du responsable marketing/communication
            </label>
            <input
              type="email"
              id="referralEmail"
              value={referralEmail}
              onChange={(e) => setReferralEmail(e.target.value)}
              placeholder="email@entreprise.com"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`
              w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium transition-colors
              ${submitting 
                ? 'bg-amber-400 cursor-not-allowed' 
                : 'bg-amber-500 hover:bg-amber-600'
              }
              text-white
            `}
          >
            {submitting ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                <span>Traitement en cours...</span>
              </>
            ) : (
              <span>Envoyer</span>
            )}
          </button>
        </form>
        
        <p className="text-xs text-gray-500 mt-6 text-center">
          Nous respectons votre vie privée. L'email ne sera utilisé que pour proposer cette analyse.
        </p>
      </div>
    </div>
  );
};

export default ReferralPage;
